import { useEffect, useState } from "react";
import { Link } from "react-router";
import SignupForm from "../components/signup-form";
import type { SignupFormData } from "../components/signup-form";
import OtpForm from "../components/otp-form";
import { useAuthStore } from "@/modules/auth/stores/auth-store";
import type { LoginRequest, SignupRequest } from "@/modules/auth/types";
import Button from "@/components/ui/button";
import IconGoogle from "@/components/icons/icon-google";
import { partnerApi } from "@/modules/partner";
import type { Partner } from "@/modules/partner";
import {
	PARTNER_KEY,
	PENDING_SIGNUP_PROFILE_KEY,
	saveLastUsedLogin,
	PartnerBadge,
	PartnerBadgeSkeleton,
} from "@/components/auth-page-shared";

type Step = "form" | "otp";

export default function SignupPage() {
	const { signup, login, validateOtp } = useAuthStore();
	const [step, setStep] = useState<Step>("form");
	const [sessionId, setSessionId] = useState<string | null>(null);
	const [email, setEmail] = useState<string>("");
	const [firstName, setFirstName] = useState<string>("");
	const [lastName, setLastName] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [accountExists, setAccountExists] = useState(false);
	const [isInvited, setIsInvited] = useState(false);
	const searchParams = new URLSearchParams(location.search);
	const redirect = searchParams.get("redirect");
	const partnerSlug = searchParams.get("partner");

	// Use URL slug if present, otherwise fall back to saved slug
	const savedSlug = localStorage.getItem(PARTNER_KEY);
	const effectiveSlug = partnerSlug || savedSlug;

	const [partner, setPartner] = useState<Partner | null>(null);
	const [partnerLoading, setPartnerLoading] = useState(!!effectiveSlug);

	const emailFromUrl = searchParams.get("email");
	const savedEmail = emailFromUrl || undefined;

	// Build query string to preserve on navigation links
	const queryString = searchParams.toString();
	const loginLink = `/login${queryString ? `?${queryString}` : ""}`;

	useEffect(() => {
		if (!effectiveSlug) return;

		partnerApi.getBySlug(effectiveSlug).then((result) => {
			if (result.success && result.data) {
				setPartner(result.data);
				localStorage.setItem(PARTNER_KEY, effectiveSlug);
			} else {
				localStorage.removeItem(PARTNER_KEY);
			}
			setPartnerLoading(false);
		});
	}, [effectiveSlug]);

	const handleSignupSubmit = async (data: SignupFormData) => {
		setIsLoading(true);
		setError(null);
		setAccountExists(false);

		try {
			// Store name for post-OTP profile update
			localStorage.setItem(PENDING_SIGNUP_PROFILE_KEY, JSON.stringify({ first_name: data.first_name, last_name: data.last_name }));

			const payload: { email: string; first_name: string; last_name: string; redirect?: string; partner_id?: string } = { email: data.email, first_name: data.first_name, last_name: data.last_name };
			if (redirect) payload.redirect = redirect;
			if (partner) payload.partner_id = partner.id;

			const result = await signup(payload);

			if (!result) {
				throw new Error("Failed to send verification code");
			}

			localStorage.removeItem(PARTNER_KEY);
			setSessionId(result.session_id);
			setEmail(data.email);
			setFirstName(data.first_name);
			setLastName(data.last_name);
			setStep("otp");
		} catch (err) {
			if (err instanceof Error && (err.message === "ACCOUNT_EXISTS" || err.message === "ACCOUNT_INVITED")) {
				const invited = err.message === "ACCOUNT_INVITED";
				setAccountExists(true);
				setIsInvited(invited);
				setEmail(data.email);
				setFirstName(data.first_name);
				setLastName(data.last_name);

				try {
					// For invited users, send first_name/last_name to login so backend updates them
					const payload: LoginRequest = { email: data.email };
					if (invited) {
						payload.first_name = data.first_name;
						payload.last_name = data.last_name;
					}
					if (redirect) payload.redirect = redirect;
					if (partner) payload.partner_id = partner.id;

					const result = await login(payload);

					if (!result) {
						throw new Error("Failed to send verification code");
					}

					localStorage.removeItem(PARTNER_KEY);
					setSessionId(result.session_id);
					setStep("otp");
				} catch (loginErr) {
					setError(loginErr instanceof Error ? loginErr.message : "An error occurred");
				}
			} else {
				setError(err instanceof Error ? err.message : "An error occurred");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleOtpSubmit = async (code: string) => {
		if (!sessionId) return;

		setIsLoading(true);
		setError(null);

		try {
			const isValid = await validateOtp({ session_id: sessionId, code });

			if (!isValid) {
				throw new Error("Invalid verification code");
			}

			saveLastUsedLogin({ method: "email", email });
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendOtp = async () => {
		setIsLoading(true);
		setError(null);

		try {
			let result;
			if (accountExists) {
				const payload: LoginRequest = { email };
				if (isInvited) {
					payload.first_name = firstName;
					payload.last_name = lastName;
				}
				if (redirect) payload.redirect = redirect;
				if (partner) payload.partner_id = partner.id;
				result = await login(payload);
			} else {
				const payload: SignupRequest = { email, first_name: firstName, last_name: lastName };
				if (redirect) payload.redirect = redirect;
				if (partner) payload.partner_id = partner.id;
				result = await signup(payload);
			}

			if (!result) {
				throw new Error("Failed to resend verification code");
			}

			setSessionId(result.session_id);
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleSignup = () => {
		saveLastUsedLogin({ method: "google" });
		localStorage.removeItem(PARTNER_KEY);

		const params = new URLSearchParams();
		if (redirect) params.set("redirect", redirect);
		if (partner) params.set("partner_id", partner.id);

		const query = params.toString();
		const baseUrl = `${import.meta.env.VITE_SERVICE_CORE_URL}/auth/google/redirect`;
		window.location.href = query ? `${baseUrl}?${query}` : baseUrl;
	};

	return (
		<div className="w-full sm:w-lg flex-col rounded-lg relative z-10 p-0 sm:p-8">
			{step === "form" ? (
				<>
					<img className="mb-6 sm:mb-8 w-12 sm:w-16" src="/images/sayaboutus-logomark-light.svg" />
					<h1 className="text-lg sm:text-xl font-semibold text-primary -leading-[1%]">Create your account</h1>
					<p className="mt-2 sm:mt-3 text-sm sm:text-base text-tertiary max-w-[640px] mb-4 sm:mb-6">
						Collect and manage testimonials from your customers, then share them beautifully to build trust and grow your brand.
					</p>

					{partnerLoading && <PartnerBadgeSkeleton />}
					{partner && !partnerLoading && <PartnerBadge partner={partner} />}

					<SignupForm
						onSubmit={handleSignupSubmit}
						isLoading={isLoading}
						error={error}
						defaultEmail={savedEmail}
					/>

					<div className="relative my-4 sm:my-6">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-gray-300"></div>
						</div>
						<div className="relative flex justify-center text-sm">
							<span className="bg-[#fafafa] px-4 text-gray-500">Or</span>
						</div>
					</div>

					<div className="relative mb-4 sm:mb-6">
						<Button variant="light" onClick={handleGoogleSignup} disabled={isLoading} className="w-full flex items-center justify-center gap-3">
							<IconGoogle />
							Create an account with Google
						</Button>
					</div>

					<p className="text-xs text-tertiary text-center mb-4 sm:mb-6">
						By creating an account, you agree to our{" "}
						<a href="https://sayabout.us/legal/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
							Terms of Service
						</a>{" "}
						and{" "}
						<a href="https://sayabout.us/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
							Privacy Policy
						</a>
						.
					</p>

					<p className="text-sm text-tertiary text-center">
						Already have an account?{" "}
						<Link to={loginLink} className="text-primary hover:underline font-medium">
							Log in
						</Link>
					</p>
				</>
			) : (
				<OtpForm email={email} onSubmit={handleOtpSubmit} onBack={() => setStep("form")} onResend={() => handleResendOtp()} isLoading={isLoading} error={error} info={accountExists ? `An account with this email already exists.${isInvited ? " This may be because someone invited you to their brand." : ""} Enter the verification code sent to your email to continue.` : null} />
			)}
		</div>
	);
}
